import { MoneyLabel } from "../../../../../components/general/MoneyLabel.tsx";
import { toDecimalFromMoney } from "../../../../../lib/Money.ts";
import { DEFAULT_CURRENCY } from "../../../../../types/constants.ts";
import type { SalesInvoicesSummary } from "./types/salesInvoice";
import { summaryBucketForCurrency } from "./utils/salesInvoicesFormat";

interface Props {
    summary: SalesInvoicesSummary | null;
    isLoading: boolean;
}

function pluralizeFacturen(count: number): string {
    return count === 1 ? "factuur" : "facturen";
}

function StatCardSkeleton() {
    return (
        <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100">
            <div className="h-4 w-24 animate-pulse rounded bg-gray-100" />
            <div className="mt-3 h-8 w-36 animate-pulse rounded bg-gray-100" />
            <div className="mt-2 h-4 w-44 animate-pulse rounded bg-gray-100" />
        </div>
    );
}

export function SalesInvoiceStatCards({ summary, isLoading }: Props) {
    if (isLoading || !summary) {
        return (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <StatCardSkeleton />
                <StatCardSkeleton />
                <StatCardSkeleton />
            </div>
        );
    }

    const cards = [
        {
            label: "Openstaand",
            dotClasses: "bg-blue-500",
            bucket: summaryBucketForCurrency(summary.outstanding, DEFAULT_CURRENCY),
            caption: `${summary.outstanding_count} ${pluralizeFacturen(summary.outstanding_count)} ${summary.outstanding_count === 1 ? "wacht" : "wachten"} op betaling`,
        },
        {
            label: "Te laat",
            dotClasses: "bg-red-500",
            bucket: summaryBucketForCurrency(summary.overdue, DEFAULT_CURRENCY),
            caption: `${summary.overdue_count} ${pluralizeFacturen(summary.overdue_count)} over de vervaldatum`,
        },
        {
            label: "Betaald deze maand",
            dotClasses: "bg-green-500",
            bucket: summaryBucketForCurrency(summary.paid_this_month, DEFAULT_CURRENCY),
            caption: `${summary.paid_this_month_count} ${pluralizeFacturen(summary.paid_this_month_count)} voldaan`,
        },
    ];

    return (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {cards.map((card) => (
                <div
                    key={card.label}
                    className="p-6 bg-white rounded-xl shadow-sm border border-gray-100"
                >
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                        <span className={`h-2 w-2 rounded-full ${card.dotClasses}`} />
                        {card.label}
                    </div>
                    <div className="mt-2 text-2xl font-semibold tracking-tight text-gray-900">
                        <MoneyLabel
                            value={toDecimalFromMoney(card.bucket.value)}
                            showCurrency={true}
                            currency={card.bucket.currency}
                        />
                    </div>
                    <p className="mt-1 text-sm text-gray-500">{card.caption}</p>
                </div>
            ))}
        </div>
    );
}
